import { useState } from 'react';
import { useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

export type BrandLink = { id: string; label: string; url: string; note: string | null; styleId: string | null };

/** Important links the AI uses for CTAs/references — global (styleId null) or per style. */
export function LinksEditor({ links, styleId, title }: { links: BrandLink[]; styleId: string | null; title: string }) {
  const { revalidate } = useRevalidator();
  const [draft, setDraft] = useState({ label: '', url: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      await api('/links', { method: 'POST', json: { label: draft.label, url: draft.url, note: draft.note || undefined, styleId } });
      setDraft({ label: '', url: '', note: '' });
      revalidate();
    } catch (err) {
      setError(errMsg(err, 'Could not add link'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    try {
      await api(`/links/${id}`, { method: 'DELETE' });
      revalidate();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded-lg border bg-white p-4 text-sm">
      <h2 className="text-base">{title}</h2>
      <p className="text-xs text-muted-foreground">The AI is told to use exactly these URLs for CTAs and references (and never to invent others).</p>
      <ul className="divide-y">
        {links.length === 0 && <li className="py-2 text-xs text-muted-foreground">No links yet.</li>}
        {links.map((l) => (
          <li key={l.id} className="flex items-center gap-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{l.label}</div>
              <a href={l.url} target="_blank" rel="noreferrer" className="ff-link block truncate text-xs">
                {l.url}
              </a>
              {l.note && <div className="text-xs text-muted-foreground">{l.note}</div>}
            </div>
            <button className="text-xs text-muted-foreground hover:text-red-600" disabled={busy} onClick={() => remove(l.id)}>
              remove
            </button>
          </li>
        ))}
      </ul>
      <div className="grid gap-2 md:grid-cols-[1fr_1.4fr_1fr_auto]">
        <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Label (e.g. Pricing page)" className="h-9 rounded-md border px-2" />
        <input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="https://…" className="h-9 rounded-md border px-2" />
        <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="When to use it" className="h-9 rounded-md border px-2" />
        <Button size="sm" onClick={add} disabled={busy || !draft.label.trim() || !/^https?:\/\//.test(draft.url)}>
          Add
        </Button>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
